import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import {
  Users,
  CreditCard,
  Workflow,
  TrendingUp,
  Activity,
  DollarSign,
  UserCheck,
  AlertCircle
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import { useQuery } from 'react-query';
import { adminAPI } from '../services/api';
import toast from 'react-hot-toast';

const DashboardContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const DashboardHeader = styled.div`
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: bold;
  color: ${props => props.theme.colors.text};
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const Subtitle = styled.p`
  color: ${props => props.theme.colors.textSecondary};
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${props => props.theme.spacing.lg};
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const StatCard = styled(motion.div)`
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.xl};
  padding: ${props => props.theme.spacing.xl};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.lg};
`;

const StatIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: ${props => props.theme.borderRadius.xl};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  background: ${props => props.color || props.theme.colors.primary};
`;

const StatContent = styled.div`
  flex: 1;
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: bold;
  color: ${props => props.theme.colors.text};
  margin-bottom: ${props => props.theme.spacing.xs};
`;

const StatLabel = styled.div`
  color: ${props => props.theme.colors.textSecondary};
  font-weight: 500;
`;

const StatChange = styled.div`
  font-size: 0.875rem;
  color: ${props => props.positive ? props.theme.colors.success : props.theme.colors.error};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  margin-top: ${props => props.theme.spacing.xs};
`;

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: ${props => props.theme.spacing.xl};
  margin-bottom: ${props => props.theme.spacing.xl};
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ChartCard = styled.div`
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.xl};
  padding: ${props => props.theme.spacing.xl};
`;

const ChartTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const RecentActivity = styled.div`
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.xl};
  padding: ${props => props.theme.spacing.xl};
`;

const ActivityItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  padding: ${props => props.theme.spacing.md} 0;
  border-bottom: 1px solid ${props => props.theme.colors.border};
  
  &:last-child {
    border-bottom: none;
  }
`;

const ActivityIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.color || props.theme.colors.background};
  color: ${props => props.iconColor || props.theme.colors.text};
`;

const ActivityContent = styled.div`
  flex: 1;
`;

const ActivityText = styled.div`
  color: ${props => props.theme.colors.text};
  margin-bottom: ${props => props.theme.spacing.xs};
`;

const ActivityTime = styled.div`
  font-size: 0.875rem;
  color: ${props => props.theme.colors.textSecondary};
`;

function Dashboard() {
  // Fetch real data
  const { data: dashboardData, isLoading, error } = useQuery('dashboardStats', adminAPI.getDashboardStats, {
    refetchInterval: 30000, // Refetch every 30 seconds
    onError: (err) => {
      toast.error('Не удалось загрузить данные');
      console.error('Dashboard error:', err);
    }
  });

  // Fetch revenue stats
  const { data: revenueStats } = useQuery('revenueStats', () => adminAPI.getRevenueStats(), {
    refetchInterval: 60000, // Refetch every minute
    onError: (err) => {
      console.error('Revenue stats error:', err);
    }
  });

  // Fetch user stats
  const { data: userStats } = useQuery('userStats', () => adminAPI.getUserStats(), {
    refetchInterval: 60000, // Refetch every minute
    onError: (err) => {
      console.error('User stats error:', err);
    }
  });

  // Fetch recent activity
  const { data: activityData } = useQuery('recentActivity', () => adminAPI.getRecentActivity(10), {
    refetchInterval: 30000, // Refetch every 30 seconds
    onError: (err) => {
      console.error('Activity error:', err);
    }
  });

  // Format stats from real data
  const stats = React.useMemo(() => {
    if (!dashboardData) {
      return [
        {
          icon: Users,
          label: 'Всего пользователей',
          value: '0',
          change: '...',
          positive: true,
          color: '#3b82f6'
        },
        {
          icon: CreditCard,
          label: 'Доход за месяц',
          value: '₽0',
          change: '...',
          positive: true,
          color: '#10b981'
        },
        {
          icon: Workflow,
          label: 'Активных задач',
          value: '0',
          change: '...',
          positive: false,
          color: '#f59e0b'
        },
        {
          icon: UserCheck,
          label: 'Рефералов',
          value: '0',
          change: '...',
          positive: true,
          color: '#8b5cf6'
        }
      ];
    }

    return [
      {
        icon: Users,
        label: 'Всего пользователей',
        value: dashboardData.totalUsers?.toLocaleString('ru-RU') || '0',
        change: '+0%',
        positive: true,
        color: '#3b82f6'
      },
      {
        icon: CreditCard,
        label: 'Доход за месяц',
        value: `₽${(dashboardData.monthlyRevenue || 0).toLocaleString('ru-RU')}`,
        change: '+0%',
        positive: true,
        color: '#10b981'
      },
      {
        icon: Workflow,
        label: 'Активных задач',
        value: dashboardData.activeJobs?.toString() || '0',
        change: '...',
        positive: false,
        color: '#f59e0b'
      },
      {
        icon: UserCheck,
        label: 'Рефералов',
        value: dashboardData.referrals?.toString() || '0',
        change: '+0%',
        positive: true,
        color: '#8b5cf6'
      }
    ];
  }, [dashboardData]);

  // Format revenue data from API
  const revenueData = React.useMemo(() => {
    if (!revenueStats?.data?.monthly_data) {
      return [
        { month: 'Янв', revenue: 0 },
        { month: 'Фев', revenue: 0 },
        { month: 'Мар', revenue: 0 },
        { month: 'Апр', revenue: 0 },
        { month: 'Май', revenue: 0 },
        { month: 'Июн', revenue: 0 }
      ];
    }
    return revenueStats.data.monthly_data.map(item => ({
      month: item.month,
      revenue: item.revenue || 0
    }));
  }, [revenueStats]);

  // Format user distribution data from API
  const userData = React.useMemo(() => {
    if (!userStats?.data?.distribution) {
      return [
        { name: 'Новые', value: 0, color: '#3b82f6' },
        { name: 'Активные', value: 0, color: '#10b981' },
        { name: 'Неактивные', value: 0, color: '#f59e0b' },
        { name: 'Заблокированные', value: 0, color: '#ef4444' }
      ];
    }
    
    return userStats.data.distribution.map(item => ({
      name: item.name,
      value: item.value || 0,
      color: item.color
    }));
  }, [userStats]);

  // Format activities from API
  const activities = React.useMemo(() => {
    if (!activityData?.data?.activities || activityData.data.activities.length === 0) {
      return [
        {
          icon: Users,
          text: 'Нет активности',
          time: '—',
          color: '#6b7280',
          iconColor: 'white'
        }
      ];
    }
    
    const iconMap = {
      'users': Users,
      'creditcard': CreditCard,
      'workflow': Workflow,
      'alert': AlertCircle
    };
    
    return activityData.data.activities.map(activity => {
      let icon = Workflow;
      let color = '#8b5cf6';
      
      if (activity.type === 'user') {
        icon = Users;
        color = '#3b82f6';
      } else if (activity.type === 'payment') {
        icon = CreditCard;
        color = '#10b981';
      } else if (activity.type === 'job') {
        icon = Workflow;
        color = activity.status === 'completed' ? '#8b5cf6' : activity.status === 'failed' ? '#ef4444' : '#f59e0b';
      }
      
      // Add username to text if available
      let displayText = activity.text;
      if (activity.username && !activity.username.startsWith('User ')) {
        displayText = `${activity.text} (@${activity.username})`;
      }
      
      return {
        icon,
        text: displayText,
        time: activity.time || '—',
        color,
        iconColor: 'white'
      };
    });
  }, [activityData]);

  if (isLoading) {
    return (
      <DashboardContainer>
        <DashboardHeader>
          <Title>Дашборд</Title>
          <Subtitle>Обзор системы и статистика</Subtitle>
        </DashboardHeader>
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <div style={{ fontSize: '18px', color: '#6b7280' }}>Загрузка данных...</div>
        </div>
      </DashboardContainer>
    );
  }

  return (
    <DashboardContainer>
      <DashboardHeader>
        <Title>Дашборд</Title>
        <Subtitle>Обзор системы и статистика</Subtitle>
      </DashboardHeader>

      <StatsGrid>
        {stats.map((stat, index) => (
          <StatCard
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.1 }}
          >
            <StatIcon color={stat.color}>
              <stat.icon size={24} />
            </StatIcon>
            <StatContent>
              <StatValue>{stat.value}</StatValue>
              <StatLabel>{stat.label}</StatLabel>
              <StatChange positive={stat.positive}>
                <TrendingUp size={14} />
                {stat.change}
              </StatChange>
            </StatContent>
          </StatCard>
        ))}
      </StatsGrid>

      <ChartsGrid>
        <ChartCard>
          <ChartTitle>Доходы по месяцам</ChartTitle>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={revenueData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="revenue" fill="#3b82f6" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard>
          <ChartTitle>Распределение пользователей</ChartTitle>
          <ResponsiveContainer width="100%" height={300}>
            {!userStats?.data ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#6b7280' }}>
                Загрузка данных...
              </div>
            ) : !userStats.data.total_users || userStats.data.total_users === 0 ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#6b7280' }}>
                Нет пользователей в системе
              </div>
            ) : userData.filter(item => item.value > 0).length === 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#6b7280', gap: '8px' }}>
                <div>Ожидание данных...</div>
                <div style={{ fontSize: '14px', color: '#9ca3af' }}>Всего пользователей: {userStats.data.total_users || 0}</div>
              </div>
            ) : (
              <PieChart>
                <Pie
                  data={userData.filter(item => item.value > 0)}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent, value }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {userData.filter(item => item.value > 0).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value, name, props) => [
                    `${value} пользователей`,
                    props.payload.name
                  ]}
                />
              </PieChart>
            )}
          </ResponsiveContainer>
        </ChartCard>
      </ChartsGrid>

      <RecentActivity>
        <ChartTitle>Последняя активность</ChartTitle>
        {activities.map((activity, index) => (
          <ActivityItem key={index}>
            <ActivityIcon color={activity.color} iconColor={activity.iconColor}>
              <activity.icon size={20} />
            </ActivityIcon>
            <ActivityContent>
              <ActivityText>{activity.text}</ActivityText>
              <ActivityTime>{activity.time}</ActivityTime>
            </ActivityContent>
          </ActivityItem>
        ))}
      </RecentActivity>
    </DashboardContainer>
  );
}

export default Dashboard;
